// src/components/student/sat/StudentTestTaker.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db, storage } from '../../../firebaseConfig';
import {
  collection, query, where, getDocs, orderBy, doc, setDoc, getDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { LuLayoutGrid, LuBookmark, LuCalculator, LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { saveDiagnosticLiveProgress, markDiagnosticLiveProgressComplete } from '../diagnostics/diagnosticLiveProgress';
import { backdropDismiss } from '../../../utils/backdropDismiss';
/**
 * ======================== CONFIG ========================
 */
const INITIAL_STAGE = 'RW_Routing';

const TIME_LIMITS = {
  RW_Routing:   32 * 60,
  RW_Module_A:  32 * 60,
  RW_Module_B:  32 * 60,
  Math_Routing: 35 * 60,
  Math_Module_A:35 * 60,
  Math_Module_B:35 * 60,
  BREAK:        10 * 60
};

const LABELS = {
  RW_Routing:   'Reading & Writing - Module 1',
  RW_Module_A:  'Reading & Writing - Module 2 (Standard)',
  RW_Module_B:  'Reading & Writing - Module 2 (Advanced)',
  Math_Routing: 'Math - Module 1',
  Math_Module_A:'Math - Module 2 (Standard)',
  Math_Module_B:'Math - Module 2 (Advanced)',
  BREAK:        '10 Minute Break'
};

function sanitizeExtendedTimePercent(value) {
  const n = Number(value);
  return [0, 25, 50, 75, 100].includes(n) ? n : 0;
}

function getSATTimeLimit(moduleKey, extendedTimePercent = 0) {
  const base = TIME_LIMITS[moduleKey] ?? 0;
  if (moduleKey === 'BREAK') return base; // break never extended
  const pct = sanitizeExtendedTimePercent(extendedTimePercent);
  return Math.round(base * (1 + pct / 100));
}

const MATH_MODULES = new Set(['Math_Routing', 'Math_Module_A', 'Math_Module_B']);

// Desmos official demo key (works for dev). For production, request your own key.
const DESMOS_API_KEY = 'dcb31709b452b1cf9dc26972add0fda6';

/**
 * ======================== STYLES ========================
 */
const HEADER_HEIGHT = 64; // keep in sync with header padding
const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'radial-gradient(circle at 20% -10%, #eff6ff 0, #f8fafc 40%, #ffffff 100%)',
  },
  

  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    maxWidth: 900,         // Slightly narrower max-width for better reading focus now that it's single col
    width: '100%',
    margin: '0 auto',
    paddingBottom: 96,      // room for fixed footer
  },
  header: {
    height: HEADER_HEIGHT,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    background: '#1c3246', // Dark official ACT blue
    color: '#fff',
    borderBottom: 'none',
    zIndex: 30,
    fontSize: 14,
    fontWeight: 600
  },
  logoRow: { display: 'flex', alignItems: 'center' },
  logoImg: { height: 36, width: 'auto', marginRight: 6 },
  logoTitle: { fontSize: 20, fontWeight: 700, letterSpacing: '0.5px', color: '#ffffff', },
  logoDivider: { height: 18, width: 1, background: '#64748b', margin: '0 16px' },
  logoSubtitle: { fontSize: 17, fontWeight: 400, color: '#e2e8f0' },
  headerTools: { display: 'flex', alignItems: 'center', gap: 20, marginLeft: 40 },
  headerBtn: {
    background: 'transparent', border: 'none', color: '#cbd5e1', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700,
    padding: '4px 8px', borderRadius: 4, transition: 'color 0.2s'
  },
  headerBtnActive: {
    color: '#1c3246', background: '#ffffff', boxShadow: '0 0 10px rgba(255,255,255,0.3)'
  },
  headerStats: { display: 'flex', alignItems: 'center', gap: 24, fontSize: 14, fontWeight: 700, letterSpacing: '0.5px' },
  statItem: { display: 'flex', alignItems: 'center', gap: 8 },

  // Instruction Screen
  instructionPage: {
    padding: '60px max(5vw, 20px)',
    maxWidth: '800px',
    margin: '0 auto',
    height: '100%',
    overflowY: 'auto'
  },
  instructionTitle: { fontSize: 26, fontWeight: 800, color: '#0f172a', marginBottom: 24 },
  instructionText: { fontSize: 18, lineHeight: 1.8, color: '#334155', whiteSpace: 'pre-wrap' },
  startSectionBtn: {
    marginTop: 40,
    padding: '14px 32px',
    fontSize: 16, fontWeight: 700,
    background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer',
    width: '100%'
  },
  warningBox: {
    marginTop: 24, padding: 16, borderRadius: 8,
    background: '#fef2f2', border: '1px solid #f87171', color: '#991b1b',
    fontWeight: 600, display: 'flex', gap: 12, alignItems: 'center'
  },

  // Left column (Question + Options)
  qWrap: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    boxShadow: '0 8px 28px rgba(17,24,39,0.06)',
    overflow: 'hidden'
  },
  qHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderBottom: '1px solid #eef2f7',
    background: '#fafafa'
  },
  qBody: { padding: 16 },
  qImgWrap: { display: 'flex', justifyContent: 'center' },
  qImg: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    margin: '0 auto',               // center image
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#f9fafb'
  },
  fallbackText: { fontSize: 14, color: '#6b7280' },

  optionsCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    boxShadow: '0 8px 28px rgba(17,24,39,0.06)',
    overflow: 'hidden'
  },
  optionsHead: {
    padding: '12px 14px',
    borderBottom: '1px solid #eef2f7',
    background: '#fafafa',
    fontWeight: 700,
    color: '#111827'
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 14
  },
  optionRow: (selected, struck) => ({
    display: 'grid',
    gridTemplateColumns: '36px 1fr auto',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 12,
    cursor: 'pointer',
    border: selected ? '2px solid #2563eb' : '1px solid #e5e7eb',
    background: selected ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#ffffff',
    boxShadow: selected ? '0 8px 24px rgba(37,99,235,0.22)' : '0 2px 8px rgba(17,24,39,0.05)',
    opacity: struck ? 0.55 : 1,
    textDecoration: struck ? 'line-through' : 'none'
  }),
  optionLabel: (selected) => ({
    width: 32, height: 32, display: 'grid', placeItems: 'center',
    borderRadius: 8, background: selected ? '#1d4ed8' : '#f3f4f6',
    color: selected ? '#ffffff' : '#111827', fontWeight: 800
  }),
  strikeBtn: (on) => ({
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid ' + (on ? '#f97316' : '#e5e7eb'),
    background: on ? 'linear-gradient(135deg,#fed7aa,#fdba74)' : '#ffffff',
    color: on ? '#7c2d12' : '#111827',
    fontSize: 12, fontWeight: 700, cursor: 'pointer'
  }),

  // Floating Calculator
  floatCalc: {
    position: 'absolute',
    zIndex: 50,
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column'
  },
  floatHeader: {
    padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    cursor: 'move', userSelect: 'none'
  },

  // Fixed footer bar
  footer: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
    borderTop: '1px solid #e5e7eb',
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(10px)',
  },
  footerInner: {
    maxWidth: 900, margin: '0 auto', // Matches main width
    padding: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10
  },
  navBtn: (primary) => ({
    padding: '10px 24px',
    borderRadius: 8,
    border: primary ? 'none' : '1px solid #cbd5e1',
    background: primary ? '#2563eb' : '#ffffff',
    color: primary ? '#ffffff' : '#0f172a',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14,
    boxShadow: primary ? '0 4px 12px rgba(37,99,235,0.2)' : 'none'
  }),

  // Grid overlay
  overlay: {
    position: 'fixed', inset: 0, zIndex: 50,
    background: 'rgba(17,24,39,0.45)',
    display: 'grid', placeItems: 'center'
  },
  gridModal: {
    width: 'min(950px, 96vw)',
    maxHeight: '80vh',
    overflow: 'hidden',
    borderRadius: 16,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    boxShadow: '0 40px 90px rgba(17,24,39,0.35)',
    display: 'flex',
    flexDirection: 'column'
  },
  gridTitle: { fontWeight: 800, fontSize: 18 },
  gridContent: {
    padding: 16,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
    gap: 10,
    overflowY: 'auto'
  },
  gridItem: (status) => ({
    display: 'grid', placeItems: 'center',
    height: 56, borderRadius: 12, cursor: 'pointer', fontWeight: 800,
    border: '1px solid #e5e7eb',
    background:
      status === 'current'    ? 'linear-gradient(135deg,#bfdbfe,#93c5fd)' :
      status === 'answered'   ? '#ecfeff' :
      status === 'bookmarked' ? '#fef3c7' :
      '#ffffff'
  }),

  // Break screen
  breakScreen: { margin: 'auto', display: 'grid', gap: 16, placeItems: 'center' },
  breakTitle: { fontSize: 28, fontWeight: 800 },
  breakTimer: { fontSize: 48, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }
};

/**
 * ======================== HELPERS ========================
 */

// Image loader with graceful fallback (centered)
const QuestionImage = ({ path }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!path) { setLoading(false); return; }
    let active = true;
    getDownloadURL(ref(storage, path))
      .then(u => { if (active) { setUrl(u); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [path]);

  if (loading) return <div style={styles.fallbackText}>Loading Image...</div>;
  if (!url)   return <div style={styles.fallbackText}>No image available for this question.</div>;
  return (
    <div style={styles.qImgWrap}>
      <img src={url} alt="Question" style={styles.qImg} />
    </div>
  );
};

// Ensure skipped answers exist as empty strings
function normalizeAnswersForQuestions(questions, answersObj) {
  const next = { ...answersObj };
  for (const q of questions || []) {
    if (!(q.questionId in next)) next[q.questionId] = "";
  }
  return next;
}

// Build normalized map for modules taken
function normalizedAnswersForModules(allQuestions, modulesTaken, answersObj) {
  const set = new Set(modulesTaken);
  const out = {};
  for (const q of allQuestions) {
    if (set.has(q.module)) out[q.questionId] = (answersObj[q.questionId] ?? "");
  }
  return out;
}

// Deterministic seed from a string (lint-safe: no mixed bitwise precedence)
function hashStringToSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0; // keep 32-bit
  }
  return h >>> 0;
}

// Simple LCG PRNG (deterministic)
function lcg(seed) {
  let s = seed >>> 0;
  return function() {
    // Numerical Recipes parameters
    s = (Math.imul(1664525, s) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function shuffleDeterministic(array, seedStr) {
  const arr = array.slice();
  const rnd = lcg(hashStringToSeed(seedStr));
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Draggable & Resizable Floating Desmos
const FloatingCalculator = ({ geometry, onUpdate, onClose }) => {
  const desmosRef = useRef(null);
  // Track dragging state: 'move', 'resize-e' (east), 'resize-s' (south), 'resize-se' (corner)
  const [dragMode, setDragMode] = useState(null); 
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startGeo, setStartGeo] = useState(null);

  // Initialize Desmos
  useEffect(() => {
    // Basic polling to ensure script is loaded
    const initCalc = () => {
      if (window.Desmos && desmosRef.current && !desmosRef.current.calculator) {
        desmosRef.current.calculator = window.Desmos.GraphingCalculator(desmosRef.current, { keypad:true, expressions:true });
      } else if (!window.Desmos) {
        setTimeout(initCalc, 50);
      }
    };
    initCalc();
  }, []);

  // Handle Global Mouse Moves for Dragging/Resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragMode) return;
      e.preventDefault();

      const dx = e.clientX - startPos.x;
      const dy = e.clientY - startPos.y;
      const next = { ...startGeo };

      if (dragMode === 'move') {
        next.x += dx;
        next.y += dy;
      } else {
        // Resizing Logic
        if (dragMode.includes('e')) next.w = Math.max(300, startGeo.w + dx); // Min width 300
        if (dragMode.includes('s')) next.h = Math.max(200, startGeo.h + dy); // Min height 200
      }

      onUpdate(next);
    };

    const handleMouseUp = () => setDragMode(null);

    if (dragMode) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragMode, startPos, startGeo, onUpdate]);

  const startDrag = (e, mode) => {
    e.stopPropagation();
    setDragMode(mode);
    setStartPos({ x: e.clientX, y: e.clientY });
    setStartGeo(geometry);
  };

  return (
    <div style={{
      ...styles.floatCalc, 
      left: geometry.x, top: geometry.y, 
      width: geometry.w, height: geometry.h 
    }}>
      {/* Header (Move Handle) */}
      <div style={styles.floatHeader} onMouseDown={(e) => startDrag(e, 'move')}>
        <span style={{fontSize:12, fontWeight:700, color:'#333'}}>Desmos Calculator</span>
        <button onClick={onClose} style={{border:'none', background:'transparent', fontSize:16, cursor:'pointer'}}>✕</button>
      </div>

      {/* Calculator Container */}
      <div ref={desmosRef} style={{flex:1, width:'100%', height:'100%', position:'relative'}} />

      {/* --- Resize Handles --- */}
      {/* Right Edge */}
      <div 
        onMouseDown={(e) => startDrag(e, 'resize-e')}
        style={{position:'absolute', top:0, right:0, bottom:10, width:8, cursor:'e-resize', zIndex:10}} 
      />
      {/* Bottom Edge */}
      <div 
        onMouseDown={(e) => startDrag(e, 'resize-s')}
        style={{position:'absolute', bottom:0, left:0, right:10, height:8, cursor:'s-resize', zIndex:10}} 
      />
      {/* Bottom-Right Corner (Diagonal) */}
      <div 
        onMouseDown={(e) => startDrag(e, 'resize-se')}
        style={{
          position:'absolute', bottom:0, right:0, width:16, height:16, 
          cursor:'se-resize', zIndex:20, background: '#cbd5e1',
          borderTopLeftRadius: 4
        }} 
      />
    </div>
  );
};

/**
 * Helper: Checks answer with numeric tolerance for Math
 * Handles fractions (e.g., "3/2") and decimals (e.g., "1.5")
 */
function isAnswerCorrect(studentInput, dbAnswerString) {
  if (!studentInput || !dbAnswerString) return false;

  const sStr = studentInput.toString().trim().toUpperCase();
  // Split DB answers by comma (e.g., "0.5, 1/2")
  const options = dbAnswerString.split(',').map(s => s.trim().toUpperCase());

  // 1. Exact String Match (Works for MCQs: "A", "B" or exact text)
  if (options.includes(sStr)) return true;

  // 2. Numeric Check (For Math)
  // Helper to parse "3/2" -> 1.5
  const parseVal = (val) => {
    if (val.includes('/')) {
      const [n, d] = val.split('/');
      return parseFloat(n) / parseFloat(d);
    }
    return parseFloat(val);
  };

  const sNum = parseVal(sStr);
  if (isNaN(sNum)) return false; // Student typed non-number text

  // Compare against every valid option in the DB
  return options.some((opt) => {
    const optNum = parseVal(opt);
    if (isNaN(optNum)) return false;
    
    // Check if difference is very small (allow 0.001 margin of error)
    return Math.abs(sNum - optNum) < 0.01;
  });
}

/**
 * ======================== MAIN ========================
 */
export default function StudentTestTaker({ testId, onExit }) {
  const [loadingState, setLoadingState] = useState(true);
  const [currentModule, setCurrentModule] = useState(INITIAL_STAGE);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMITS[INITIAL_STAGE]);
  const [extendedTimePercent, setExtendedTimePercent] = useState(0);
  const [viewingInstructions, setViewingInstructions] = useState(true);
  const [agreeIntegrity, setAgreeIntegrity] = useState(false);
  const [recOn, setRecOn] = useState(true);

  // --- Integrity Tracking State ---
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  const [totalInactiveTime, setTotalInactiveTime] = useState(0);
  
  const blurTimeRef = useRef(null);
  const isInactiveRef = useRef(false);

  // Shuffled questions for display
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [loadingQ, setLoadingQ] = useState(false);

  const [answers, setAnswers] = useState({});
  const [bookmarks, setBookmarks] = useState({});
  const [strikeMap, setStrikeMap] = useState({}); // { [questionId]: {A:true, B:false, ...} }

  const [pathHistory, setPathHistory] = useState([]);
  const [showGrid, setShowGrid] = useState(false);

  // Calculator open/close (Math only)
  const [calcOpen, setCalcOpen] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [calcGeo, setCalcGeo] = useState({ x: 100, y: 100, w: 500, h: 400 });

  const userId = auth.currentUser?.uid;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const formatMinutesLabel = (seconds) => {
    const mins = seconds / 60;
    return Number.isInteger(mins) ? `${mins} minutes` : `${mins.toFixed(1)} minutes`;
  };

  const satTimeLabel = (moduleKey) => {
    const seconds = getSATTimeLimit(moduleKey, extendedTimePercent);
    const isExtended = extendedTimePercent > 0 && moduleKey !== 'BREAK';
    return `${formatMinutesLabel(seconds)}${isExtended ? ' (Extended)' : ''}`;
  };

  // --- Pre-load the Desmos script *once* so calculator shows immediately on first open
  useEffect(() => {
    if (document.querySelector('script[data-desmos-api="1.7"]')) return;
    const s = document.createElement('script');
    s.src = `https://www.desmos.com/api/v1.7/calculator.js?apiKey=${encodeURIComponent(DESMOS_API_KEY)}`;
    s.async = true;
    s.defer = true;
    s.setAttribute('data-desmos-api', '1.7');
    document.body.appendChild(s);
  }, []);

  // ---- 1) Resume
  useEffect(() => {
    const initTest = async () => {
      if (!userId) return;
      const accessRef = doc(db, "student_test_access", `${userId}_${testId}`);
      let extPercent = 0;

      try {
        const accessSnap = await getDoc(accessRef);
        if (accessSnap.exists()) {
          extPercent = sanitizeExtendedTimePercent(accessSnap.data().extendedTimePercent);
        }
      } catch (err) {
        console.warn("Failed to load extended time setting:", err);
      }

      setExtendedTimePercent(extPercent);

      const resumeRef = doc(db, "diagnostic_results", userId, "state", "testState");
      try {
        const snap = await getDoc(resumeRef);
        if (snap.exists() && snap.data().testId === testId) {
          const data = snap.data();
          setCurrentModule(data.currentModule || INITIAL_STAGE);
          setViewingInstructions(false); // Skip instructions if resuming a saved session
          setAgreeIntegrity(false);
          setTimeLeft(
            typeof data.timeLeft === 'number'
              ? data.timeLeft
              : getSATTimeLimit(data.currentModule || INITIAL_STAGE, extPercent)
          );
          if (data.answers)   setAnswers(data.answers);
          if (data.bookmarks) setBookmarks(data.bookmarks);
          if (data.strikeMap) setStrikeMap(data.strikeMap);
          if (data.currentQuestionIndex) setQIdx(data.currentQuestionIndex);
          if (data.pathHistory) setPathHistory(data.pathHistory);
          if (data.integrityLog) {
            setTabSwitchCount(data.integrityLog.tabSwitchCount || 0);
            setFullscreenExitCount(data.integrityLog.fullscreenExitCount || 0);
            setTotalInactiveTime(data.integrityLog.totalInactiveTime || 0);
          }
        } else {
          setTimeLeft(getSATTimeLimit(INITIAL_STAGE, extPercent));
        }

      } catch (err) {
        console.error("Resume failed:", err);
      } finally {
        setLoadingState(false);
      }
    };
    initTest();
  }, [userId, testId]);

  // ---- 2) Load questions for module, then deterministically shuffle for display
  useEffect(() => {
    if (loadingState) return;
    if (currentModule === 'BREAK' || currentModule === 'FINISH') {
      setQuestions([]); return;
    }
    const loadQuestions = async () => {
      setLoadingQ(true);
      try {
        const qy = query(
          collection(db, "sat_diagnostic_tests", testId, "questions"),
          where("module", "==", currentModule),
          orderBy("questionNumber")
        );
        const snap = await getDocs(qy);
        const base = snap.docs.map(d => d.data());
        const seed = `${userId || ''}__${testId || ''}__${currentModule}`;
        const shuffled = shuffleDeterministic(base, seed);
        setQuestions(shuffled);
        setQIdx(prev => (shuffled.length > 0 && prev < shuffled.length ? prev : 0));
      } catch (err) {
        console.error("Load Error:", err);
      } finally {
        setLoadingQ(false);
      }
    };
    loadQuestions();
  }, [currentModule, testId, loadingState, userId]);

  // Close calculator when switching out of Math sections
  useEffect(() => {
    if (!MATH_MODULES.has(currentModule)) setCalcOpen(false);
  }, [currentModule]);

  // ---- 3) Autosave state (includes strikeMap & normalized answers)
  const saveTimeout = useRef(null);
  useEffect(() => {
    if (loadingState || viewingInstructions) return; // Prevent autosave on instruction screen
    const doSave = async () => {
      if (!userId) return;
      const normalized = normalizeAnswersForQuestions(questions, answers);
      const resumeRef = doc(db, "diagnostic_results", userId, "state", "testState");
      try {
        await setDoc(resumeRef, {
          testId,
          currentModule,
          currentQuestionIndex: qIdx,
          answers: normalized,
          bookmarks,
          strikeMap,
          timeLeft,
          pathHistory,
          integrityLog: {
            tabSwitchCount,
            fullscreenExitCount,
            totalInactiveTime
          },
          updatedAt: serverTimestamp()
        });
        // Additive: publish a flat, queryable summary for the Admin HomeScreen
        // "Diagnostic Test Progress" dashboard. Never blocks the test.
        saveDiagnosticLiveProgress({
          testType: 'SAT',
          testId,
          studentId: userId,
          studentName: auth.currentUser?.displayName || '',
          stageKey: currentModule,
          answeredCount: questions.reduce((acc, q) => (normalized[q.questionId] ? acc + 1 : acc), 0),
          totalQuestions: questions.length,
          sectionTimeLeftSeconds: timeLeft,
          pathHistory,
        });
        if (normalized !== answers) setAnswers(normalized);
      } catch (err) {
        console.warn("Autosave failed", err);
      }
    };
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(doSave, 1000);
    return () => clearTimeout(saveTimeout.current);
  }, [qIdx, answers, bookmarks, strikeMap, timeLeft, currentModule, pathHistory, testId, userId, loadingState, viewingInstructions, questions, tabSwitchCount, fullscreenExitCount, totalInactiveTime]);
  // ---- 4) Scoring / branching
  const calculateCurrentScore = useCallback(() => {
    let correct = 0;
    questions.forEach(q => {
      const studentAns = answers[q.questionId];
      if (isAnswerCorrect(studentAns, q.correctAnswer)) correct++;
    });
    return correct;
  }, [questions, answers]);

  const getNextModule = (current, score) => {
    if (current === 'RW_Routing') return score >= 19 ? 'RW_Module_B' : 'RW_Module_A';
    if (current === 'RW_Module_A' || current === 'RW_Module_B') return 'BREAK';
    if (current === 'BREAK') return 'Math_Routing';
    if (current === 'Math_Routing') return score >= 14 ? 'Math_Module_B' : 'Math_Module_A';
    if (current === 'Math_Module_A' || current === 'Math_Module_B') return 'FINISH';
    return 'FINISH';
  };

  // ---- 5) Finish flow (writes fully normalized map so skipped Qs exist as "")
  const finishTest = useCallback(async (finalHistory) => {
    if (!userId) return;
    setLoadingState(true);

    const allQSnap = await getDocs(collection(db, "sat_diagnostic_tests", testId, "questions"));
    const allQs = allQSnap.docs.map(d => d.data());

    let rwRaw = 0;
    let mathRaw = 0;
    const modulesTaken = finalHistory.map(h => h.module);
    const takenSet = new Set(modulesTaken);

    allQs.forEach(q => {
      if (!takenSet.has(q.module)) return;
      const studentAns = answers[q.questionId];
      if (isAnswerCorrect(studentAns, q.correctAnswer)) {
        if (q.module.includes('RW') || q.module.includes('English')) rwRaw++;
        if (q.module.includes('Math')) mathRaw++;
      }
    });

    const rwSnap   = await getDoc(doc(db, "dsat_rw_scores",   String(rwRaw)));
    const mathSnap = await getDoc(doc(db, "dsat_math_scores", String(mathRaw)));
    const tookRwHard   = takenSet.has('RW_Module_B');
    const tookMathHard = takenSet.has('Math_Module_B');

    const rwScaled   = rwSnap.exists()   ? (tookRwHard ? rwSnap.data().moduleB : rwSnap.data().moduleA) : 200;
    const mathScaled = mathSnap.exists() ? (tookMathHard ? mathSnap.data().moduleB : mathSnap.data().moduleA) : 200;
    const totalScore = (rwScaled || 200) + (mathScaled || 200);

    const normalizedAll = normalizedAnswersForModules(allQs, modulesTaken, answers);

    // FIX: Use a composite ID (userId_testId) so multiple tests don't overwrite each other
    const resultDocId = `${userId}_${testId}`;

    await setDoc(doc(db, "diagnostic_results", resultDocId), {
      studentId: userId,
      type: "SAT",
      testId,
      status: 'completed',
      completedAt: serverTimestamp(),
      rwRawScore: rwRaw,
      mathRawScore: mathRaw,
      rwScaledScore: rwScaled,
      mathScaledScore: mathScaled,
      totalScore,
      answers: normalizedAll,
      pathHistory: finalHistory,
      integrityLog: {
        tabSwitchCount,
        fullscreenExitCount,
        totalInactiveTime,
        cheated_triggered: (tabSwitchCount > 2 || totalInactiveTime > 30 || fullscreenExitCount > 2)
      }
    });

    await setDoc(
      doc(db, "student_test_access", `${userId}_${testId}`),
      { finished: true, finishedAt: serverTimestamp() },
      { merge: true }
    );

    await deleteDoc(doc(db, "diagnostic_results", userId, "state", "testState"));
    // Mark the live-progress summary completed so the Admin HomeScreen can show
    // it as done and link straight to the diagnostic report.
    markDiagnosticLiveProgressComplete({
      testType: 'SAT',
      testId,
      studentId: userId,
      attemptId: resultDocId,
      scoreSummary: { totalScore, rwScaledScore: rwScaled, mathScaledScore: mathScaled },
    });
    alert("Test Complete! Your responses have been submitted.");
    onExit();
  }, [userId, testId, answers, onExit, tabSwitchCount, fullscreenExitCount, totalInactiveTime]);

  // Confirmation before moving to next section
  const goToNextSectionWithConfirm = () => {
    setShowConfirmModal(true); // Open custom modal instead of native confirm
  };

  const handleNextStage = useCallback(async () => {
    const normalized = normalizeAnswersForQuestions(questions, answers);
    if (normalized !== answers) setAnswers(normalized);

    const score = calculateCurrentScore();
    const nextModule = getNextModule(currentModule, score);
    const newHistory = [...pathHistory, { module: currentModule, score }];
    setPathHistory(newHistory);

    // Write per-module snapshot with normalized answers
    const responseId = `${auth.currentUser?.uid}_${testId}_${currentModule}`;
    const moduleAnswers = {};
    for (const q of questions) moduleAnswers[q.questionId] = normalized[q.questionId] ?? "";

    await setDoc(doc(db, "student_responses", responseId), {
      studentId: auth.currentUser?.uid,
      testId,
      module: currentModule,
      answers: moduleAnswers,
      completedAt: serverTimestamp()
    });

    if (nextModule === 'FINISH') {
      finishTest(newHistory);
    } else {
      setCurrentModule(nextModule);
      setTimeLeft(getSATTimeLimit(nextModule, extendedTimePercent));
      setQIdx(0);
      window.scrollTo(0, 0);
    }
  }, [questions, answers, calculateCurrentScore, currentModule, pathHistory, testId, finishTest, extendedTimePercent]);

  // ---- 6) Timer & keys
  useEffect(() => {
    if (loadingState || viewingInstructions) return; // Prevent timer on instruction screen
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { handleNextStage(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [handleNextStage, loadingState, viewingInstructions]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') if (qIdx < questions.length - 1) setQIdx(qIdx + 1);
      if (e.key === 'ArrowLeft')  if (qIdx > 0) setQIdx(qIdx - 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [qIdx, questions.length]);

  useEffect(() => {
    const id = setInterval(() => setRecOn(prev => !prev), 2000); // 2.0s blink
    return () => clearInterval(id);
  }, []);
  
  // ---- Integrity Tracking Hook ----
  useEffect(() => {
    if (loadingState || viewingInstructions || currentModule === 'FINISH') return;

    const handleInactivate = () => {
      if (isInactiveRef.current) return;
      isInactiveRef.current = true;
      blurTimeRef.current = Date.now();
      // Removed setTabSwitchCount from here
    };

    const handleActivate = () => {
      if (!isInactiveRef.current) return;
      isInactiveRef.current = false;
      if (blurTimeRef.current) {
        const awayTimeSec = Math.floor((Date.now() - blurTimeRef.current) / 1000);
        setTotalInactiveTime(prev => prev + awayTimeSec);
        
        // Only count as a tab switch if they were gone for 2 or more seconds
        if (awayTimeSec >= 2) {
          setTabSwitchCount(prev => prev + 1);
        }
        
        blurTimeRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) handleInactivate();
      else handleActivate();
    };

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setFullscreenExitCount(prev => prev + 1);
      }
    };

    // Attach listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleInactivate);
    window.addEventListener("focus", handleActivate);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleInactivate);
      window.removeEventListener("focus", handleActivate);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [loadingState, viewingInstructions, currentModule]);

  // ---- 7) Answer / bookmark / strike handlers
  const handleAnswer = (val) => {
    if (!questions[qIdx]) return;
    const qId = questions[qIdx].questionId;
    setAnswers(prev => ({ ...prev, [qId]: val }));
  };
  const toggleBookmark = () => {
    if (!questions[qIdx]) return;
    const qId = questions[qIdx].questionId;
    setBookmarks(prev => {
      const next = { ...prev };
      if (next[qId]) delete next[qId]; else next[qId] = true;
      return next;
    });
  };
  const toggleStrike = (qId, opt) => {
    setStrikeMap(prev => {
      const q = { ...(prev[qId] || {}) };
      q[opt] = !q[opt];
      return { ...prev, [qId]: q };
    });
  };

  /**
   * ======================== RENDER ========================
   */
  if (loadingState) return <div style={{ padding: 40, textAlign: 'center' }}>Restoring Session...</div>;

  // 1. INSTRUCTIONS SCREEN (Runs once at the beginning)
  if (viewingInstructions) {
    const handleStartTest = async () => {
      // Guard: Prevent starting if questions haven't loaded yet
      if (questions.length === 0) {
        alert("Please wait a moment while your test questions load.");
        return;
      }
      // Force fullscreen mode
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((e) => console.log("Fullscreen blocked:", e));
      }
      
      // Store initial student_responses right when they actually start
      const responseId = `${auth.currentUser?.uid}_${testId}_${currentModule}`;
      const normalized = normalizeAnswersForQuestions(questions, answers);
      const moduleAnswers = {};
      for (const q of questions) moduleAnswers[q.questionId] = normalized[q.questionId] ?? "";

      try {
        await setDoc(doc(db, "student_responses", responseId), {
          studentId: auth.currentUser?.uid,
          testId,
          module: currentModule,
          answers: moduleAnswers,
          startedAt: serverTimestamp() // Mark exactly when they started
        }, { merge: true });
      } catch (err) {
        console.error("Failed to initialize student_responses:", err);
      }

      setViewingInstructions(false);
    };

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.logoRow}>
            <img src="/images/logo.png" alt="Logo" style={styles.logoImg} />
            <span style={styles.logoTitle}>True Education</span>
            <div style={styles.logoDivider}></div>
            <span style={styles.logoSubtitle}>SAT Assessment</span>
          </div>
        </div>

        <div style={styles.instructionPage}>
          <div style={styles.instructionTitle}><strong>Are you sure you want to take this test?</strong></div>
          
          <div style={styles.instructionText}>
            The total test time is <strong>
              {formatMinutesLabel(
                getSATTimeLimit('RW_Routing', extendedTimePercent) +
                getSATTimeLimit('RW_Module_A', extendedTimePercent) +
                getSATTimeLimit('BREAK', extendedTimePercent) +
                getSATTimeLimit('Math_Routing', extendedTimePercent) +
                getSATTimeLimit('Math_Module_A', extendedTimePercent)
              )}
            </strong>.<br/><br/>
            • Reading & Writing Module 1: <strong>{satTimeLabel('RW_Routing')}</strong><br/>
            • Reading & Writing Module 2: <strong>{satTimeLabel('RW_Module_A')}</strong><br/>
            • Break: <strong>{satTimeLabel('BREAK')}</strong><br/>
            • Math Module 1: <strong>{satTimeLabel('Math_Routing')}</strong><br/>
            • Math Module 2: <strong>{satTimeLabel('Math_Module_A')}</strong><br/><br/>
            ⚠️ <strong>Once you begin, you cannot pause or exit.</strong>
          </div>

          <div style={styles.warningBox}>
            <div style={{fontSize: 24}}>🚨</div>
            <div>
              <strong>OFFICIAL TEST SECURITY NOTICE</strong><br/>
              This SAT diagnostic session is actively monitored in real time.<br/>
              <br/>
              <strong>DO NOT</strong> switch tabs, minimize the window, open other applications, or exit full-screen mode.<br/>
              These actions are automatically detected and permanently logged to your test record.<br/>
              <br/>
              <strong>Repeated violations will trigger immediate submission</strong> and an <strong>Integrity Flag</strong> on your score report.<br/>
              Start only if you are ready to test without interruption.<br/>
            </div>
          </div>

          <label style={{ display: 'flex', gap: 10, marginTop: 14, fontSize: 14, color: '#334155', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={agreeIntegrity}
              onChange={(e) => setAgreeIntegrity(e.target.checked)}
              style={{ marginTop: 4 }}
            />
            <span>
              I understand that this session is monitored and that leaving the test page or exiting full-screen may result in automatic submission and a flagged score report.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 12, marginTop: 40 }}>
            <button
              type="button"
              style={{
                flex: 1,
                padding: '14px 32px',
                fontSize: 16,
                fontWeight: 700,
                background: '#ffffff',
                color: '#0f172a',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                cursor: 'pointer'
              }}
              onClick={onExit}
            >
              Cancel / Go Back
            </button>

            <button
              style={{
                ...styles.startSectionBtn,
                flex: 2,
                marginTop: 0,
                opacity: (agreeIntegrity && questions.length > 0) ? 1 : 0.6,
                cursor: (agreeIntegrity && questions.length > 0) ? 'pointer' : 'not-allowed'
              }}
              onClick={handleStartTest}
              disabled={!agreeIntegrity || questions.length === 0}
            >
              {questions.length === 0 ? 'Loading Questions...' : 'Start SAT Test'}
            </button>
          </div>

        </div>
      </div>
    );
  }

  const answeredCount = questions.reduce((acc, q) => {
    return answers[q.questionId] ? acc + 1 : acc;
  }, 0);

  if (currentModule === 'BREAK') {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.logoRow}>
            <img src="/images/logo.png" alt="Logo" style={styles.logoImg} />
            <span style={styles.logoTitle}>True Education</span>
            <div style={styles.logoDivider}></div>
            <span style={styles.logoSubtitle}>10 Minute Break</span>
          </div>
        </div>
        <div style={styles.breakScreen}>
          <div style={styles.breakTitle}>10 Minute Break</div>
          <div style={styles.breakTimer}>{formatTime(timeLeft)}</div>
          <button style={styles.navBtn(true)} onClick={handleNextStage}>Skip Break</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[qIdx];
  const currentVal = currentQ ? (answers[currentQ.questionId] || '') : '';
  const isBookmarked = currentQ ? !!bookmarks[currentQ.questionId] : false;
  const strikes = currentQ ? (strikeMap[currentQ.questionId] || {}) : {};
  const validMC = ['A','B','C','D'];
  const isMCQ = currentQ && validMC.includes((currentQ.correctAnswer || '').trim().toUpperCase());
  const isMath = MATH_MODULES.has(currentModule);

  // Layout is now always single column
  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={{display:'flex', alignItems:'center'}}>
          <div style={styles.logoRow}>
            <img src="/images/logo.png" alt="Logo" style={styles.logoImg} />
            <span style={styles.logoTitle}>True Education</span>
            <div style={styles.logoDivider}></div>
            <span style={styles.logoSubtitle}>{LABELS[currentModule]}</span>
          </div>

          {/* Tools */}
          <div style={styles.headerTools}>
            <button 
              style={{...styles.headerBtn, ...(showGrid ? styles.headerBtnActive : {})}} 
              onClick={() => setShowGrid(!showGrid)}
            >
              <LuLayoutGrid size={16} /> View All
            </button>

            <button 
               style={{...styles.headerBtn, ...(isBookmarked ? styles.headerBtnActive : {})}} 
               onClick={toggleBookmark}
            >
              <LuBookmark size={16} /> BOOKMARK
            </button>

            {isMath && (
              <button 
                style={{...styles.headerBtn, ...(calcOpen ? styles.headerBtnActive : {})}} 
                onClick={() => setCalcOpen(!calcOpen)}
              >
                <LuCalculator size={16} /> CALCULATOR
              </button>
            )}
          </div>
        </div>

        {/* Right Side: Stats & Timer */}
        <div style={styles.headerStats}>
          <div style={styles.statItem}>

            {/* Fake Recording Indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#f01523', fontSize: 13, fontWeight: 800, paddingRight: 16, borderRight: '1px solid #334155' }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: '#f01523',
                  opacity: recOn ? 1 : 0.25,
                  boxShadow: recOn ? '0 0 10px #f01523' : '0 0 0px transparent',
                  transition: 'opacity 800ms ease, box-shadow 800ms ease'
                }}
              ></div>
              REC
            </div>

            <span style={{color: '#94a3b8'}}>ANSWERED</span>
            <span>{answeredCount} of {questions.length}</span>
          </div>
          
          <div style={styles.statItem}>
            <span style={{color: '#94a3b8'}}>TOTAL TIME LEFT</span>
            <span style={{fontVariantNumeric: 'tabular-nums', visibility: showTimer ? 'visible' : 'hidden'}}>
              {formatTime(timeLeft)}
            </span>
            <button 
              onClick={() => setShowTimer(!showTimer)}
              style={{background:'none', border:'none', color:'#fff', cursor:'pointer', padding:4, display:'flex'}}
            >
              {showTimer ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div style={styles.main}>
          {/* Question + Options Stack */}
          <div style={{display:'grid', gap:16}}>
            <div style={styles.qWrap}>
              <div style={styles.qHead}>
                <div style={{fontWeight:700}}>Question {qIdx + 1}</div>
                <div style={{fontSize:12, color:'#6b7280'}}>Original # {currentQ?.questionNumber ?? '-'}</div>
              </div>
              <div style={styles.qBody}>
                {loadingQ ? (
                  <div style={{margin:'12px 0', color:'#6b7280'}}>Loading...</div>
                ) : (
                  currentQ ? <QuestionImage path={currentQ.imagePath} /> : <div style={styles.fallbackText}>No question.</div>
                )}
              </div>
            </div>

            <div style={styles.optionsCard}>
              <div style={styles.optionsHead}>Your Answer</div>
              <div style={styles.optionsContainer}>
                {isMCQ ? (
                  ['A','B','C','D'].map(opt => {
                    const selected = currentVal === opt;
                    const struck = !!strikes[opt];
                    return (
                      <div
                        key={opt}
                        style={styles.optionRow(selected, struck)}
                        onClick={() => handleAnswer(opt)}
                        title={struck ? 'This option is struck out' : 'Select this option'}
                      >
                        <div style={styles.optionLabel(selected)}>{opt}</div>
                        <div style={{fontWeight:700, color: struck ? '#6b7280' : '#111827'}}>
                          Option {opt}
                        </div>
                        <button
                          type="button"
                          style={styles.strikeBtn(struck)}
                          onClick={(e) => { e.stopPropagation(); toggleStrike(currentQ.questionId, opt); }}
                        >
                          {struck ? 'Un-strike' : 'Strike'}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div style={{display:'grid', gap:8}}>
                    <label style={{fontSize:13, color:'#4b5563'}}>Enter your answer:</label>
                    <input
                      type="text"
                      value={currentVal}
                      onChange={(e) => handleAnswer(e.target.value)}
                      placeholder="Type your answer"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: 12,
                        border: '1px solid #e5e7eb',
                        background: '#f9fafb',
                        fontSize: 14
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

          </div>
      </div>

      {/* Fixed Footer */}
      <div style={styles.footer}>
        <div style={styles.footerInner}>
          
          {/* Left Side: Keyboard Hints */}
          <div style={{color: '#64748b', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6}}>
            Use 
            <kbd style={{fontFamily:'inherit', border:'1px solid #cbd5e1', padding:'2px 6px', borderRadius:4, display:'inline-flex', alignItems:'center', background:'#f8fafc', color:'#334155'}}>
              <LuArrowLeft size={14} />
            </kbd> 
            <kbd style={{fontFamily:'inherit', border:'1px solid #cbd5e1', padding:'2px 6px', borderRadius:4, display:'inline-flex', alignItems:'center', background:'#f8fafc', color:'#334155'}}>
              <LuArrowRight size={14} />
            </kbd> 
            for quick nav
          </div>

          {/* Right Side: Grouped Buttons */}
          <div style={{display: 'flex', gap: 12}}>
            <button
              style={styles.navBtn(false)}
              disabled={qIdx === 0}
              onClick={() => setQIdx(idx => Math.max(idx - 1, 0))}
            >
              Previous
            </button>

            <button
              style={styles.navBtn(true)}
              onClick={() => {
                if (qIdx < questions.length - 1) {
                  setQIdx(qIdx + 1);
                } else {
                  goToNextSectionWithConfirm();
                }
              }}
            >
              {qIdx === questions.length - 1 ? 'Submit' : 'Next'}
            </button>
          </div>

        </div>
      </div>

      {/* TOOLS */}
      {calcOpen && isMath && (
        <FloatingCalculator 
            geometry={calcGeo} 
            onUpdate={setCalcGeo} 
            onClose={() => setCalcOpen(false)} 
        />
      )}

      {/* Custom Confirm Modal */}
      {showConfirmModal && (
        <div style={styles.overlay}>
          <div style={{...styles.gridModal, width: 400, padding: '32px 24px', textAlign: 'center', margin: 'auto'}}>
            <h3 style={{marginTop: 0, fontSize: 20, color: '#0f172a'}}>Submit Section?</h3>
            <p style={{color: '#475569', marginBottom: 24, lineHeight: 1.5}}>
              Are you sure you want to submit this section and move on? You will not be able to return to these questions.
            </p>
            <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
              <button 
                style={{...styles.navBtn(false), flex: 1}} 
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button 
                style={{...styles.navBtn(true), flex: 1}} 
                onClick={() => {
                  setShowConfirmModal(false);
                  handleNextStage();
                }}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review grid */}
      {showGrid && (
        <div style={styles.overlay} {...backdropDismiss(() => setShowGrid(false))}>
          <div style={styles.gridModal} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:16, borderBottom:'1px solid #eef2f7'}}>
              <div style={styles.gridTitle}>Question Review</div>
              <button onClick={() => setShowGrid(false)} style={{background:'none', border:'none', cursor:'pointer', fontSize:20}}>✕</button>
            </div>
            <div style={styles.gridContent}>
              {questions.map((q, idx) => {
                const isAns  = !!answers[q.questionId];
                const isMark = !!bookmarks[q.questionId];
                const isCurr = idx === qIdx;
                let status = 'unanswered';
                if (isAns)  status = 'answered';
                if (isMark) status = 'bookmarked';
                if (isCurr) status = 'current';
                return (
                  <div
                    key={q.questionId}
                    style={styles.gridItem(status)}
                    onClick={() => { setQIdx(idx); setShowGrid(false); }}
                    title={`Original # ${q.questionNumber}`}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Utilities */
function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}